import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { toast } from "react-toastify";
import { Loader2, BellRing, UserCheck, MessageSquareText, ShieldCheck, Save } from "lucide-react";

import { Button } from "../../components/ui/button";
import { Switch } from "../../components/ui/switch";
import { Textarea } from "../../components/ui/textarea";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../../components/ui/select";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import usePermissions from "../../hooks/usePermissions";

// Regras de ligação da empresa. Ficam em Settings (chaves calls*), lidas
// pelo backend a cada ligação — mudar aqui vale na próxima chamada.

const Bloco = ({ icon: Icon, titulo, descricao, children }) => (
	<div className="rounded-xl border bg-card p-4">
		<div className="flex gap-3">
			<Icon className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" />
			<div className="min-w-0 flex-1 space-y-3">
				<div>
					<p className="text-sm font-medium">{titulo}</p>
					<p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">{descricao}</p>
				</div>
				{children}
			</div>
		</div>
	</div>
);

const Configuracoes = () => {
	const { temPermissao } = usePermissions();
	const podeEditar = temPermissao("system:settings");
	const [valores, setValores] = useState(null);
	const [mensagem, setMensagem] = useState("");
	const [salvando, setSalvando] = useState(null);

	useEffect(() => {
		api
			.get("/settings")
			.then(({ data }) => {
				const ler = (k) => (data || []).find((s) => s.key === k)?.value || "";
				setValores({
					callsRingScope: ler("callsRingScope") || "all",
					callsAssignTicket: ler("callsAssignTicket") || "enabled",
					callsMissedMessage: ler("callsMissedMessage"),
				});
				setMensagem(ler("callsMissedMessage"));
			})
			.catch((err) => {
				toastError(err);
				setValores({ callsRingScope: "all", callsAssignTicket: "enabled", callsMissedMessage: "" });
			});
	}, []);

	const gravar = async (chave, valor, aviso) => {
		setSalvando(chave);
		try {
			await api.put(`/settings/${chave}`, { value: valor });
			setValores((v) => ({ ...v, [chave]: valor }));
			toast.success(aviso || "Configuração salva.");
		} catch (err) {
			toastError(err);
		}
		setSalvando(null);
	};

	if (!valores) {
		return (
			<div className="flex justify-center py-16">
				<Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
			</div>
		);
	}

	const mensagemMudou = mensagem.trim() !== (valores.callsMissedMessage || "").trim();

	return (
		<div className="max-w-3xl space-y-3">
			{!podeEditar && (
				<p className="rounded-lg bg-muted p-3 text-xs text-muted-foreground">
					Só quem pode alterar as configurações da empresa muda estas regras. Você está vendo como estão.
				</p>
			)}

			<Bloco
				icon={BellRing}
				titulo="Quem ouve o toque"
				descricao="Quando um cliente liga, o telefone toca no navegador de quem estiver online. Quem atender primeiro fica com a ligação; nos outros, o toque para."
			>
				<Select
					value={valores.callsRingScope}
					disabled={!podeEditar || salvando === "callsRingScope"}
					onValueChange={(v) => gravar("callsRingScope", v)}
				>
					<SelectTrigger className="max-w-md">
						<SelectValue />
					</SelectTrigger>
					<SelectContent>
						<SelectItem value="all">Toda a equipe com permissão de atender</SelectItem>
						<SelectItem value="queues">Só quem atende as filas da conexão (e administradores)</SelectItem>
					</SelectContent>
				</Select>
				<p className="text-xs text-muted-foreground">
					Conexão sem fila nenhuma toca para todos, para a ligação não ficar sem ninguém.
				</p>
			</Bloco>

			<Bloco
				icon={UserCheck}
				titulo="Atender assume o atendimento"
				descricao="Quem atende a ligação (ou liga de um atendimento aguardando) passa a ser o responsável pela conversa. Atendimento já aberto com outra pessoa não muda de dono."
			>
				<label className="flex items-center gap-2 text-sm">
					<Switch
						checked={valores.callsAssignTicket !== "disabled"}
						disabled={!podeEditar || salvando === "callsAssignTicket"}
						onCheckedChange={(v) => gravar("callsAssignTicket", v ? "enabled" : "disabled")}
					/>
					{valores.callsAssignTicket !== "disabled" ? "Ligado" : "Desligado"}
				</label>
			</Bloco>

			<Bloco
				icon={MessageSquareText}
				titulo="Mensagem para ligação perdida"
				descricao="Enviada no WhatsApp para quem ligou e ninguém atendeu, pela mesma conexão. Deixe em branco para não enviar nada."
			>
				<Textarea
					rows={3}
					placeholder="Ex.: Oi! Vimos sua ligação e já retornamos. Se preferir, escreva por aqui."
					value={mensagem}
					disabled={!podeEditar}
					onChange={(e) => setMensagem(e.target.value)}
				/>
				<div className="flex items-center justify-between gap-2">
					<p className="text-xs text-muted-foreground">
						{valores.callsMissedMessage ? "Ativa." : "Desativada (em branco)."} Não vale para grupos.
					</p>
					<Button
						size="sm"
						disabled={!podeEditar || !mensagemMudou || salvando === "callsMissedMessage"}
						onClick={() =>
							gravar(
								"callsMissedMessage",
								mensagem.trim(),
								mensagem.trim() ? "Mensagem de ligação perdida salva." : "Mensagem desativada."
							)
						}
					>
						{salvando === "callsMissedMessage" ? (
							<Loader2 className="h-3.5 w-3.5 animate-spin" />
						) : (
							<Save className="h-3.5 w-3.5" />
						)}
						Salvar
					</Button>
				</div>
			</Bloco>

			<Bloco
				icon={ShieldCheck}
				titulo="Quem pode ligar, atender e ouvir gravações"
				descricao="É permissão de cargo: Fazer ligações, Atender ligações e Ver ligações da equipe e gravações. Sem a última, cada pessoa vê só as próprias ligações."
			>
				<Button asChild size="sm" variant="outline">
					<Link to="/roles">Abrir cargos e permissões</Link>
				</Button>
			</Bloco>
		</div>
	);
};

export default Configuracoes;
